import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

import { BuscaPrêmios, FiltrosAvancados, TabsPrêmios } from './components'
import Container from '@/components/container'

import { usePrêmiosLoja } from '../../../hooks/API/premio/usePrêmiosLoja'
import { useFiltrosPrêmios } from './hooks/useFiltrosPrêmios'
import { useFiltrosReducer } from './hooks/useFiltrosReducer'
import { useTabsPrêmios } from './hooks/useTabsPrêmios'

import { ContainerAppUi } from '@/componentes/ContainerAppUi'
import { Popup } from '@/componentes/Popup/Popup'
import { UseMenuActive } from '@/hooks/useMenuAtivo'

const ITEMS_PER_PAGE = 10

export const PrêmioPágina = () => {
  UseMenuActive('Loja')

  const {
    searchBarParams,
    setSearchParams,
    currentPage,
    setCurrentPage,
    localFilters,
    updateLocalFilter,
    applyFilters,
    clearFilters,
  } = useFiltrosPrêmios()

  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  // Usar o hook personalizado para gerenciar filtros
  const {
    state: filtrosState,
    updateFromUrl,
    setBusca,
    setValorMinimo,
    setValorMaximo,
  } = useFiltrosReducer()

  const hndleApplyFilters = () => {
    setIsDrawerOpen(false)
    applyFilters()
  }

  const hndleClearFilters = () => {
    setIsDrawerOpen(false)
    clearFilters()
  }

  // Extrair valores dos parâmetros da URL
  const tipo = searchBarParams.get('premio-por') || ''
  const rankingTipo = searchBarParams.get('ranking-tipo') || 'geral'
  const orderBy = searchBarParams.get('order-by') || 'nome'
  const orderDirection = searchBarParams.get('order-direction') || 'asc'
  const valorMinimo = searchBarParams.get('valor-minimo') || ''
  const valorMaximo = searchBarParams.get('valor-maximo') || ''
  const disponivel = searchBarParams.get('disponivel')
  const categoria = searchBarParams.get('categoria')
  const posicaoMinima = searchBarParams.get('posicao-minima')
  const posicaoMaxima = searchBarParams.get('posicao-maxima')

  const { activeTab, setActiveTab } = useTabsPrêmios(disponivel)

  const formContext = useForm<{
    busca: string
    valorMinimo: string
    valorMaximo: string
  }>()
  const { watch, setValue } = formContext

  const buscaWatch = watch('busca')
  const valorMinimoWatch = watch('valorMinimo')
  const valorMaximoWatch = watch('valorMaximo')

  // Atualizar estado dos filtros quando os parâmetros da URL mudarem
  useEffect(() => {
    updateFromUrl({
      tipo,
      rankingTipo,
      orderBy: orderBy as 'nome' | 'valorEmPontos',
      orderDirection: orderDirection as 'asc' | 'desc',
      valorMinimo,
      valorMaximo,
      disponivel,
      categoria,
      posicaoMinima,
      posicaoMaxima,
    })
  }, [
    tipo,
    rankingTipo,
    orderBy,
    orderDirection,
    valorMinimo,
    valorMaximo,
    disponivel,
    categoria,
    posicaoMinima,
    posicaoMaxima,
    updateFromUrl,
  ])

  // Atualizar estado quando os valores do formulário mudarem
  useEffect(() => {
    if (buscaWatch !== filtrosState.busca) {
      setBusca(buscaWatch || '')
    }
  }, [buscaWatch, filtrosState.busca, setBusca])

  useEffect(() => {
    if (valorMinimoWatch !== filtrosState.valorMinimo) {
      setValorMinimo(valorMinimoWatch || '')
    }
  }, [valorMinimoWatch, filtrosState.valorMinimo, setValorMinimo])

  useEffect(() => {
    if (valorMaximoWatch !== filtrosState.valorMaximo) {
      setValorMaximo(valorMaximoWatch || '')
    }
  }, [valorMaximoWatch, filtrosState.valorMaximo, setValorMaximo])

  // Filtros para a API - agora usando o estado do reducer
  const apiFilters = {
    skip: (currentPage - 1) * ITEMS_PER_PAGE,
    take: ITEMS_PER_PAGE,
    tipoResgate: filtrosState.tipo || undefined,
    rankingTipo:
      filtrosState.tipo === 'RANKING' ? filtrosState.rankingTipo : undefined,
    nome: filtrosState.busca || undefined,
    valorMinimo: filtrosState.valorMinimo
      ? Number(filtrosState.valorMinimo)
      : undefined,
    valorMaximo: filtrosState.valorMaximo
      ? Number(filtrosState.valorMaximo)
      : undefined,
    isEstoque:
      filtrosState.disponivel === 'false'
        ? false
        : filtrosState.disponivel === 'true'
          ? true
          : true,
    isResgatado: false,
    orderBy: filtrosState.orderBy,
    orderDirection: filtrosState.orderDirection,
    categoriaPremiosId: filtrosState.categoria || undefined,
    posicaoRankingMin: filtrosState.posicaoMinima
      ? Number(filtrosState.posicaoMinima)
      : undefined,
    posicaoRankingMax: filtrosState.posicaoMaxima
      ? Number(filtrosState.posicaoMaxima)
      : undefined,
  }

  const { data: premiosData, isLoading: isLoadingByRanking } = usePrêmiosLoja({
    ...apiFilters,
    disponivel: filtrosState.disponivel || 'all',
  } as any)

  const premios = premiosData?.data?.data || []
  const totalPremios = premiosData?.data?.count || 0
  const totalPages = Math.ceil(totalPremios / ITEMS_PER_PAGE)

  function onSubmit() {
    if (typeof buscaWatch !== 'undefined') {
      localStorage.setItem('searchParam', buscaWatch)
    }
    setCurrentPage(1) // Reset to first page when searching
  }

  useEffect(() => {
    setValue('busca', localStorage.getItem('searchParam') || '')
    setValue('valorMinimo', valorMinimo)
    setValue('valorMaximo', valorMaximo)
    const timer = setTimeout(() => {
      localStorage.removeItem('searchParam')
    }, 1000)
    return () => clearTimeout(timer)
  }, [setValue, valorMinimo, valorMaximo])

  // Watch for changes in value range inputs and update URL params
  useEffect(() => {
    if (valorMinimoWatch !== valorMinimo) {
      setSearchParams((params) => {
        if (valorMinimoWatch) {
          params.set('valor-minimo', valorMinimoWatch)
        } else {
          params.delete('valor-minimo')
        }
        return params
      })
      setCurrentPage(1)
    }
  }, [valorMinimoWatch, valorMinimo, setSearchParams, setCurrentPage])

  useEffect(() => {
    if (valorMaximoWatch !== valorMaximo) {
      setSearchParams((params) => {
        if (valorMaximoWatch) {
          params.set('valor-maximo', valorMaximoWatch)
        } else {
          params.delete('valor-maximo')
        }
        return params
      })
      setCurrentPage(1)
    }
  }, [valorMaximoWatch, valorMaximo, setSearchParams, setCurrentPage])

  return (
    <Container className='h-full items-center gap-7 bg-esforce-black-100'>
      <ContainerAppUi className='flex h-full flex-col gap-6 py-0'>
        <BuscaPrêmios onSubmit={onSubmit} />

        <FiltrosAvancados
          isDrawerOpen={isDrawerOpen}
          setIsDrawerOpen={setIsDrawerOpen}
          localFilters={localFilters}
          updateLocalFilter={updateLocalFilter}
          applyFilters={hndleApplyFilters}
          clearFilters={hndleClearFilters}
        />

        <TabsPrêmios
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          premios={premios}
          isLoadingByRanking={isLoadingByRanking}
          currentPage={currentPage}
          totalPages={totalPages}
          setCurrentPage={setCurrentPage}
          setSearchParams={setSearchParams}
          buscaWatch={buscaWatch}
        />
      </ContainerAppUi>

      <Popup />
    </Container>
  )
}
